import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  IContactFormDataForAPI,
  IContactPayloadForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import { toast } from 'react-toastify';
import { saveContact, updateContact } from 'src/services/client';
import {
  handleContactsAPIResponse,
  handleImageUpload,
  transformContactFormDataForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { buildContactsPayloadContactsPage } from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/helpers';

export interface UseContactAPIOperationsContactsReturn {
  isLoading: boolean;
  response: any;
  error: string | null;
  
  onCreateContact: (formData: IContactFormData) => Promise<boolean>;
  onEditContact: (formData: IContactFormData) => Promise<boolean>;
  
  clearResponse: () => void;
}

export const useContactAPIOperationsContacts = (): UseContactAPIOperationsContactsReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const adminUser = useSelector(selectAuthenticatedUser);
  const formOptions = useSelector(selectFormOptions);
  
  const onCreateContact = async (
    formData: IContactFormData
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const imageUrl = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);
      const payload: IContactPayloadForAPI = buildContactsPayloadContactsPage(
        apiFormData,
        adminUser.id,
        formOptions
      );
      
      const responseData = await saveContact(payload);
      return handleContactsAPIResponse(responseData, true, setResponse, setError);
    } catch (err: any) {
      console.error('Contact creation failed:', err);
      
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      setError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const onEditContact = async (
    formData: IContactFormData
  ): Promise<boolean> => {
    if (!formData.id) {
      const error = 'Cannot edit contact: Missing contact ID';
      console.error(error);
      toast.error(`🚫 ${error}`, {
        autoClose: 8000,
        position: 'top-center',
      });
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const imageUrl = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI =
        transformContactFormDataForAPI(formData, imageUrl);
      
      const payload: IContactPayloadForAPI = buildContactsPayloadContactsPage(
        apiFormData,
        adminUser.id,
        formOptions
      );
      
      const responseData = await updateContact(payload);
      return handleContactsAPIResponse(responseData, true, setResponse, setError);
    } catch (err: any) {
      console.error('Contact update failed:', err);
      
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      setError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const clearResponse = () => {
    setResponse(null);
    setError(null);
  };
  
  return {
    isLoading,
    response,
    error,
    onCreateContact,
    onEditContact,
    clearResponse
  };
};